import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import ActionGroup from "./ActionGroup";
import {
  greyServiceLinks,
  navigationLinks,
  storeLinks,
  studioLinks,
  utilityActions,
} from "../siteContent";

const Nav = () => {
  const location = useLocation();
  const greyDropdownRef = useRef(null);
  const mobileGreyDropdownRef = useRef(null);
  const [greyMenuOpen, setGreyMenuOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [storeOpen, setStoreOpen] = useState(false);
  const headerNavigationLinks = navigationLinks.filter((link) => link.to !== "/");
  const headerActions = utilityActions.filter((action) => action.label !== "Instagram");
  const greyMenuActive = greyServiceLinks.some(
    (link) => location.pathname === link.to
  );

  const closeGreyMenu = () => {
    setGreyMenuOpen(false);
  };

  const closeMenu = () => {
    setMenuOpen(false);
    closeGreyMenu();
  };

  const openStore = () => {
    setStoreOpen(true);
    closeMenu();
  };

  const closeStore = () => {
    setStoreOpen(false);
  };

  useEffect(() => {
    if (!storeOpen && !greyMenuOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closeStore();
        closeGreyMenu();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [greyMenuOpen, storeOpen]);

  useEffect(() => {
    closeGreyMenu();
  }, [location.pathname]);

  useEffect(() => {
    if (!greyMenuOpen) return undefined;

    const handleMouseDown = (event) => {
      const clickedDesktopDropdown =
        greyDropdownRef.current &&
        greyDropdownRef.current.contains(event.target);
      const clickedMobileDropdown =
        mobileGreyDropdownRef.current &&
        mobileGreyDropdownRef.current.contains(event.target);

      if (!clickedDesktopDropdown && !clickedMobileDropdown) {
        closeGreyMenu();
      }
    };

    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [greyMenuOpen]);

  return (
    <header className="site-header">
      <div className="container nav-shell">
        <Link aria-label="Lizzie Fox Colour Studio home" className="brand-link" to="/">
          <img
            alt="Lizzie Fox Colour Studio"
            className="brand-link__logo"
            src="/branding/logo-dark.svg"
          />
        </Link>

        <nav className="desktop-nav">
          {headerNavigationLinks.map((link) => {
            if (link.to === "/the-grey-transition") {
              return (
                <div className="nav-dropdown" key={link.to} ref={greyDropdownRef}>
                  <button
                    aria-expanded={greyMenuOpen}
                    aria-haspopup="true"
                    className={`nav-link nav-link--button nav-dropdown__trigger ${
                      greyMenuActive ? "active" : ""
                    }`.trim()}
                    onClick={() => setGreyMenuOpen((current) => !current)}
                    type="button"
                  >
                    Grey Transition
                    <span
                      aria-hidden="true"
                      className={`nav-dropdown__chevron ${
                        greyMenuOpen ? "is-open" : ""
                      }`.trim()}
                    />
                  </button>

                  <div
                    className={`nav-dropdown__menu ${
                      greyMenuOpen ? "is-open" : ""
                    }`.trim()}
                    role="menu"
                  >
                    {greyServiceLinks.map((serviceLink) => (
                      <NavLink
                        className={({ isActive }) =>
                          `nav-dropdown__item ${isActive ? "active" : ""}`.trim()
                        }
                        key={serviceLink.to}
                        onClick={closeGreyMenu}
                        role="menuitem"
                        to={serviceLink.to}
                      >
                        <span>{serviceLink.label}</span>
                        <p>{serviceLink.description}</p>
                      </NavLink>
                    ))}
                  </div>
                </div>
              );
            }

            return (
              <NavLink
                className={({ isActive }) =>
                  isActive ? "nav-link active" : "nav-link"
                }
                key={link.to}
                to={link.to}
              >
                {link.label}
              </NavLink>
            );
          })}
          <button
            className="nav-link nav-link--button"
            onClick={openStore}
            type="button"
          >
            Store
          </button>
        </nav>

        <div className="desktop-utility">
          <ActionGroup actions={headerActions} className="desktop-actions" />
          <a
            aria-label="Visit Lizzie Fox Colour Studio on Instagram"
            className="instagram-icon-link"
            href={studioLinks.instagram}
            rel="noreferrer"
            target="_blank"
          >
            <img alt="" aria-hidden="true" src="/images/instagram.png" />
          </a>
        </div>

        <button
          aria-expanded={menuOpen}
          aria-label="Toggle navigation"
          className={`menu-toggle ${menuOpen ? "is-open" : ""}`}
          onClick={() => setMenuOpen((current) => !current)}
          type="button"
        >
          <span />
          <span />
          <span />
        </button>
      </div>

      <div className={`mobile-menu ${menuOpen ? "is-open" : ""}`}>
        <div className="container mobile-menu__panel">
          <div className="mobile-menu__links">
            {headerNavigationLinks.map((link) => {
              if (link.to === "/the-grey-transition") {
                return (
                  <div
                    className="mobile-nav-group"
                    key={link.to}
                    ref={mobileGreyDropdownRef}
                  >
                    <button
                      aria-expanded={greyMenuOpen}
                      aria-haspopup="true"
                      className={`mobile-nav-link nav-link--button mobile-nav-dropdown__trigger ${
                        greyMenuActive ? "active" : ""
                      }`.trim()}
                      onClick={() => setGreyMenuOpen((current) => !current)}
                      type="button"
                    >
                      Grey Transition
                      <span
                        aria-hidden="true"
                        className={`nav-dropdown__chevron ${
                          greyMenuOpen ? "is-open" : ""
                        }`.trim()}
                      />
                    </button>

                    <div
                      className={`mobile-nav-submenu ${
                        greyMenuOpen ? "is-open" : ""
                      }`.trim()}
                    >
                      {greyServiceLinks.map((serviceLink) => (
                        <NavLink
                          className={({ isActive }) =>
                            `mobile-nav-sublink ${
                              isActive ? "active" : ""
                            }`.trim()
                          }
                          key={serviceLink.to}
                          onClick={closeMenu}
                          to={serviceLink.to}
                        >
                          {serviceLink.label}
                        </NavLink>
                      ))}
                    </div>
                  </div>
                );
              }

              return (
                <NavLink
                  className={({ isActive }) =>
                    isActive ? "mobile-nav-link active" : "mobile-nav-link"
                  }
                  key={link.to}
                  onClick={closeMenu}
                  to={link.to}
                >
                  {link.label}
                </NavLink>
              );
            })}
            <button
              className="mobile-nav-link nav-link--button"
              onClick={openStore}
              type="button"
            >
              Store
            </button>
          </div>

          <div className="mobile-utility">
            <ActionGroup actions={headerActions} className="mobile-actions" />
            <a
              aria-label="Visit Lizzie Fox Colour Studio on Instagram"
              className="instagram-icon-link instagram-icon-link--mobile"
              href={studioLinks.instagram}
              rel="noreferrer"
              target="_blank"
            >
              <img alt="" aria-hidden="true" src="/images/instagram.png" />
            </a>
          </div>
        </div>
      </div>

      {storeOpen && (
        <div
          aria-labelledby="store-modal-title"
          aria-modal="true"
          className="store-modal"
          onMouseDown={closeStore}
          role="dialog"
        >
          <div
            className="store-modal__panel"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <button
              aria-label="Close store options"
              className="store-modal__close"
              onClick={closeStore}
              type="button"
            >
              <span />
              <span />
            </button>

            <p className="section-eyebrow">Store</p>
            <h2 id="store-modal-title">Where would you like to go?</h2>

            <div className="store-modal__options">
              {storeLinks.map((link) => (
                <a
                  className="store-modal__option"
                  href={link.href}
                  key={link.href}
                  onClick={closeStore}
                  rel="noreferrer"
                  target="_blank"
                >
                  <span>{link.title}</span>
                  <p>{link.description}</p>
                </a>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Nav;
